// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useState, useEffect, useRef } from 'react';
import { Call } from '@azure/communication-calling';
import { VideoUploadService } from './videoUploadService';
import './VideoProctoring.css';

interface VideoProctoringProps {
  sessionId: string;
  interviewId: string;
  call: Call;
  tabAudioStream?: MediaStream;
}

const VideoProctoring: React.FC<VideoProctoringProps> = ({ sessionId, interviewId, call, tabAudioStream }) => {
  // State
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const uploadServiceRef = useRef<VideoUploadService | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const recordingSessionId = sessionId || interviewId;

  /**
   * Initialize video capture on mount
   */
  useEffect(() => {
    initializeVideoCapture();

    return () => {
      cleanup();
    };
  }, []);

  /**
   * Cleanup tab audio stream on unmount
   */
  useEffect(() => {
    return () => {
      if (tabAudioStream) {
        console.log('[VideoRecording] Stopping tab audio stream on cleanup');
        tabAudioStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [tabAudioStream]);

  /**
   * Start recording when stream is ready
   */
  useEffect(() => {
    if (stream && !isRecording) {
      startRecording();
    }
  }, [stream, isRecording]);

  /**
   * Ensure video element plays when stream is available
   */
  useEffect(() => {
    if (stream && videoRef.current) {
      const videoElement = videoRef.current;

      if (videoElement.srcObject !== stream) {
        videoElement.srcObject = stream;
      }

      const ensurePlayback = async (): Promise<void> => {
        if (videoElement.paused) {
          try {
            await videoElement.play();
          } catch (err) {
            console.error('[VideoRecording] Failed to ensure playback:', err);
          }
        }
      };

      ensurePlayback();
    }
  }, [stream]);

  /**
   * Cleanup when user navigates away or closes tab
   */
  useEffect(() => {
    const handleBeforeUnload = (): void => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [stream]);

  /**
   * Initialize video capture and mix audio sources
   */
  const initializeVideoCapture = async (): Promise<void> => {
    try {
      setIsLoading(true);
      console.log('[VideoRecording] Initializing recording with agent audio...');

      // Step 1: Get camera and microphone
      const userMedia = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 15 },
          facingMode: 'user'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      console.log('[VideoRecording] ✅ Camera and microphone captured');

      // Step 2: Use pre-captured tab audio (from ReadinessCheck)
      if (tabAudioStream && tabAudioStream.getAudioTracks().length > 0) {
        console.log('[VideoRecording] ✅ Using pre-captured tab audio (includes agent voice)');
      } else {
        console.warn('[VideoRecording] ⚠️ No tab audio stream provided - agent voice will NOT be recorded');
      }

      // Step 3: Mix audio using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;
      const destination = audioContext.createMediaStreamDestination();

      // Add microphone
      const micSource = audioContext.createMediaStreamSource(userMedia);
      const micGain = audioContext.createGain();
      micGain.gain.value = 1.0;
      micSource.connect(micGain);
      micGain.connect(destination);
      console.log('[VideoRecording] ✅ Microphone added to mix');

      // Add tab audio if captured
      if (tabAudioStream && tabAudioStream.getAudioTracks().length > 0) {
        const tabAudioSource = audioContext.createMediaStreamSource(tabAudioStream);
        const tabGain = audioContext.createGain();
        tabGain.gain.value = 1.0; // Same volume as mic
        tabAudioSource.connect(tabGain);
        tabGain.connect(destination);
        console.log('[VideoRecording] ✅ Tab audio (agent voice) added to mix');
      } else {
        console.warn('[VideoRecording] ⚠️ Agent voice NOT in recording!');
      }

      // Step 4: Create final recording stream
      const finalStream = new MediaStream();

      // Add video from user media
      userMedia.getVideoTracks().forEach((track) => {
        finalStream.addTrack(track);
      });

      // Add mixed audio
      destination.stream.getAudioTracks().forEach((track) => {
        finalStream.addTrack(track);
      });

      console.log('[VideoRecording] Final recording stream created:');
      console.log('  - Video tracks:', finalStream.getVideoTracks().length);
      console.log('  - Audio tracks:', finalStream.getAudioTracks().length);
      console.log('  - Includes agent audio:', tabAudioStream !== null);

      // Set video preview
      if (videoRef.current) {
        videoRef.current.srcObject = userMedia;

        await new Promise<void>((resolve, reject) => {
          if (!videoRef.current) {
            reject(new Error('Video element not available'));
            return;
          }

          const videoElement = videoRef.current;
          videoElement.onloadedmetadata = () => resolve();
          videoElement.onerror = () => reject(new Error('Video element failed'));
          setTimeout(() => reject(new Error('Timeout')), 5000);
        });

        await videoRef.current.play();
        console.log('[VideoRecording] Video preview started');
      }

      streamRef.current = finalStream;
      setStream(finalStream);

      // Initialize video upload service
      uploadServiceRef.current = new VideoUploadService();

      setIsLoading(false);
      console.log('[VideoRecording] ✅ Recording initialized successfully');
    } catch (error: any) {
      setIsLoading(false);
      console.error('[VideoRecording] ❌ Failed to initialize:', error);
      setError('Camera access denied. Interview will continue without video recording.');
    }
  };

  /**
   * Start video recording with chunked upload
   */
  const startRecording = (): void => {
    if (stream && uploadServiceRef.current && !isRecording && recordingSessionId) {
      uploadServiceRef.current.startRecording(stream, recordingSessionId);
      setIsRecording(true);
      console.log('[VideoRecording] ✅ Recording started');
    }
  };

  /**
   * Cleanup resources
   */
  const cleanup = async (): Promise<void> => {
    console.log('[VideoRecording] Starting cleanup...');

    if (uploadServiceRef.current) {
      try {
        await uploadServiceRef.current.stopRecording();
      } catch (error) {
        console.error('[VideoRecording] Error stopping recording:', error);
      }
      uploadServiceRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    const activeStream = streamRef.current || stream;
    if (activeStream) {
      activeStream.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
      setStream(null);
    }

    if (videoRef.current) {
      videoRef.current.onloadedmetadata = null;
      videoRef.current.onerror = null;
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }

    setIsRecording(false);
    setIsLoading(false);

    console.log('[VideoRecording] ✅ Cleanup complete');
  };

  if (error) {
    return null;
  }

  if (!stream) {
    return null;
  }

  return (
    <div className="video-recording-container">
      <div className="video-preview-wrapper">
        <video ref={videoRef} autoPlay muted playsInline className="video-preview" />

        {isLoading && (
          <div className="recording-loading">
            <div className="recording-spinner" />
            <span>Starting camera...</span>
          </div>
        )}

        <div className="recording-corner-badge">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 7l-7 5 7 5V7z" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
          Cam
        </div>

        <div className="video-preview-footer">
          <div className="recording-live-tag">
            <span className="recording-dot" />
            LIVE
          </div>
          <div className="recording-candidate-name">Recording</div>
        </div>
      </div>
    </div>
  );
};

export default VideoProctoring;
